import {
  SCHEMA_BY_NAME,
  SearchParameterFieldKind,
  isArrayKind,
  kindOf,
  type SearchParameterElement,
  type SearchParameterName,
  type SearchParameterValues,
} from './schema';

/**
 * The transformations behind SearchParameters, as pure functions over a raw
 * value bag.
 *
 * They live apart from the class so the rules can be read and tested without a
 * SearchParameters instance in the way, and because they must address
 * parameters by their schema name — the class getters rename six of them and
 * coerce four others, so they cannot be used here.
 */

type Values = Record<string, unknown>;

/**
 * The one place a computed key is asserted back onto the schema type.
 * TypeScript widens `{ [name]: value }` to an index signature when `name` is
 * generic, so it cannot carry the name/value pairing through. That pairing is
 * checked instead by the signatures of the exported functions below.
 */
const toPatch = (
  name: SearchParameterName,
  value: unknown
): Partial<SearchParameterValues> =>
  ({ [name]: value } as Partial<SearchParameterValues>);

/**
 * Removing a parameter means falling back to its default, not leaving a hole.
 * Writing `undefined` over the key would keep it present, which drops
 * defaulted parameters out of the serialized URL — see #shouldDropValue in
 * ./serialization/serialize.
 */
// Removing a parameter removes it. A row's `defaultValue` is restored by the
// getter that reads it, not written back into the values — putting it back here
// would make a cleared parameter look stated again.
const withoutName = (values: Values, name: string): Values => {
  const next = { ...values };
  delete next[name];
  return next;
};

/**
 * The values with every `undefined` entry dropped, so a key set to `undefined`
 * reads as unstated rather than as stated and empty.
 *
 * Every change below already deletes a key rather than writing `undefined`, so
 * this guards the one way in that does not go through them: a search built
 * directly from a value bag, where `{ orientations: undefined }` would
 * otherwise overwrite the schema's empty list and make the getter answer
 * `undefined` against its own type.
 */
export const definedValues = (
  values: SearchParameterValues
): SearchParameterValues =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined)
  ) as SearchParameterValues;

/**
 * The kind of a parameter one value at a time can meaningfully address.
 *
 * Agreements are excluded: the parameter holds a group of four, so no single
 * value stands for it.
 */
const toggleableKindOf = (
  name: SearchParameterName
): SearchParameterFieldKind => {
  const kind = kindOf(name);
  if (kind === SearchParameterFieldKind.AGREEMENTS) {
    throw new Error(
      `${name} holds a group of values rather than one — change it with with({ ${name} })`
    );
  }
  return kind;
};

/**
 * Whether the parameter holds several values.
 *
 * Read from the schema rather than from whether the current value happens to
 * be an array, so an array parameter that is currently unset is not mistaken
 * for a scalar one.
 */
const holdsSeveralValues = (name: SearchParameterName): boolean =>
  isArrayKind(toggleableKindOf(name));

const elementsOf = <N extends SearchParameterName>(
  values: Values,
  name: N
): SearchParameterElement<N>[] =>
  (values[name] ?? []) as SearchParameterElement<N>[];

/**
 * A value in the shape its schema row declares.
 *
 * `fromQuery` splits array parameters on commas, but `with()` is also handed
 * values that were never a query string. The license Select offers whole
 * combinations as single options, so picking "RF and RM" arrives here as the
 * one token `'rf,rm'` rather than a list. Splitting it keeps what is stored
 * matching the schema however the value arrived — without this, an array
 * parameter can hold a string, and every reader that treats it as a list
 * breaks on the next access.
 *
 * Mirrors `SearchParameterDeserializer#splitStringList`: trimmed, blanks
 * dropped, an empty string yielding an empty list.
 */
const inSchemaShape = (name: string, value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const row = SCHEMA_BY_NAME.get(name);
  if (!row || !isArrayKind(row.kind)) return value;
  return value
    .split(',')
    .map((element) => element.trim())
    .filter(Boolean);
};

const isEmptyList = (value: unknown): boolean =>
  Array.isArray(value) && value.length === 0;

/**
 * Sets each named parameter; `undefined` clears instead of setting, and so does
 * an empty list.
 *
 * An empty list is no choice: an array parameter holding none of something is
 * what it means when unstated, so storing `[]` would make `states()` report a
 * choice the visitor never made — and preference fill skips a parameter the
 * search already states. That covers a blank string for an array parameter
 * too, which `inSchemaShape` turns into an empty list. The same rule the
 * deserializer applies to an all-invalid list, so `with()` and `fromQuery()`
 * agree.
 */
export const withValues = (
  values: SearchParameterValues,
  changes: Partial<SearchParameterValues>
): SearchParameterValues => {
  let next: Values = { ...values };
  for (const [name, value] of Object.entries(changes)) {
    const shaped = value === undefined ? undefined : inSchemaShape(name, value);
    if (shaped === undefined || isEmptyList(shaped)) {
      next = withoutName(next, name);
    } else {
      next[name] = shaped;
    }
  }
  return next as SearchParameterValues;
};

/**
 * Replaces one parameter's value outright, rather than toggling it.
 *
 * What a single-choice filter needs: picking the option already chosen leaves
 * it chosen, where `toggledValue` would clear it.
 */
export const setValue = <N extends SearchParameterName>(
  values: SearchParameterValues,
  name: N,
  value: SearchParameterValues[N] | undefined
): SearchParameterValues => withValues(values, toPatch(name, value));

/** Removes each named parameter, restoring its schema default if it has one. */
export const clearedValues = (
  values: SearchParameterValues,
  names: readonly SearchParameterName[]
): SearchParameterValues =>
  names.reduce<Values>(withoutName, { ...values }) as SearchParameterValues;

/** Whether `toggledValue` would remove `value` rather than add it. */
export const isValueSelected = <N extends SearchParameterName>(
  values: SearchParameterValues,
  name: N,
  value: SearchParameterElement<N>
): boolean =>
  holdsSeveralValues(name)
    ? elementsOf(values, name).includes(value)
    : values[name] === value;

/**
 * Adds `value` if absent, removes it if present. Parameters holding several
 * values toggle membership; every other kind toggles the whole value.
 */
export const toggledValue = <N extends SearchParameterName>(
  values: SearchParameterValues,
  name: N,
  value: SearchParameterElement<N>
): SearchParameterValues => {
  if (!holdsSeveralValues(name)) {
    return isValueSelected(values, name, value)
      ? clearedValues(values, [name])
      : withValues(values, toPatch(name, value));
  }

  const current = elementsOf(values, name);
  const next = current.includes(value)
    ? current.filter((element) => element !== value)
    : [...current, value];

  return next.length
    ? withValues(values, toPatch(name, next))
    : clearedValues(values, [name]);
};
